/* eslint-disable react-hooks/exhaustive-deps */
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { cloneDeep } from "lodash";
import React, { useState } from "react";
import { Button, Offcanvas } from "react-bootstrap";

import { EActionItem, EToastvariant } from "../../../../config/interfaces";
import { useAppDispatch } from "../../../../redux/Store";
import { showToastMsg } from "../../../../redux/slice/ToastMsgSlice";
import { UtilService } from "../../../../services";
import FASvgIcon from "../../../FASvgIcon";
import FileUploadDropzone from "../../../FileUploadDropzone";
import { ConfirmationModal } from "../../../modals";
import { OverlaySpinner } from "../../../placeholders";
import { UploadedDocument } from "../../assisted";

type Props = {
  showCanvas: boolean;
  handleCloseBtnPress: () => void;
};

const AISUploadForm: React.FC<Props> = (props: Props) => {
  const { showCanvas, handleCloseBtnPress } = props;

  const dispatch = useAppDispatch();

  const [selectedDocuments, setSelectedDocuments] = useState<any[]>([]);
  const [deleteDocModal, setDeleteDocModal] = useState<any | null>(null);
  const [isAction, setIsAction] = useState<EActionItem>(EActionItem.NULL);

  const createSelectedFilesData = async (files: File[]) => {
    try {
      for (let index = 0; index < files.length; index++) {
        const file = files[index];

        const fileData: any = {
          id: `${index}`,
          uuid: `${new Date().getTime()}`,
          fileName: file.name,
          file: file,
          result: null
        };

        setSelectedDocuments((prev) => [...prev, fileData]);
      }
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    }
  };

  const handleDeleteDocument = async () => {
    try {
      if (!deleteDocModal) {
        return;
      }

      setIsAction(EActionItem.DELETE);
      const _selectedDocuments = cloneDeep(selectedDocuments);
      _selectedDocuments.splice(Number(deleteDocModal.id), 1);
      setSelectedDocuments(() => _selectedDocuments);

      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Deleted",
          message: `${deleteDocModal?.fileName} deleted successfully`,
          variant: EToastvariant.Success
        })
      );
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setIsAction(EActionItem.NULL);
      setDeleteDocModal(null);
    }
  };

  const handleExtractStocksDetailsData = async () => {
    try {
      if (!selectedDocuments?.length) {
        return;
      }

      setIsAction(EActionItem.CREATE);

      handleCloseBtnPress();
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setIsAction(EActionItem.NULL);
      setDeleteDocModal(null);
    }
  };

  return (
    <>
      <Offcanvas show={showCanvas} placement="end" backdrop={true}>
        <Offcanvas.Header>
          <Offcanvas.Title>Upload Annual Info Statement to Auto-Fill Your Data</Offcanvas.Title>
          <Button
            variant="primary"
            className="icon-only hide-offcanvas"
            onClick={() => {
              handleCloseBtnPress();
              setTimeout(() => {
                setSelectedDocuments(() => []);
              }, 200);
            }}
          >
            <FASvgIcon icon={faXmark} />
          </Button>
        </Offcanvas.Header>

        <Offcanvas.Body>
          <section className="mb-5">
            {selectedDocuments?.map((doc, index) => (
              <UploadedDocument
                key={`stocks-details-${index}`}
                uuid={doc.uuid}
                fileName={doc.fileName}
                showDeleteBtn={true}
                handleOnPressDelete={() => setDeleteDocModal(doc)}
                showViewBtn={true}
                handleOnPressView={() => {
                  const file = doc.file as Blob;
                  const fileUrl = URL.createObjectURL(file);
                  const newWindow = window.open(fileUrl, "_blank", "noopener, noreferrer");
                  if (newWindow) {
                    newWindow.opener = null;
                    URL.revokeObjectURL(fileUrl);
                  }
                }}
              />
            ))}

            {!selectedDocuments?.length ? (
              <FileUploadDropzone
                multiple={false}
                allowedFileTypes={[".pdf"]}
                handleOnFileSelect={(files) => createSelectedFilesData(files)}
              />
            ) : null}
          </section>
        </Offcanvas.Body>
        <div className="offcanvas-btns">
          <Button
            variant="outline-white"
            size="sm"
            onClick={() => handleExtractStocksDetailsData()}
            disabled={!selectedDocuments?.length || isAction === EActionItem.CREATE}
          >
            Continue
          </Button>
        </div>
      </Offcanvas>

      {deleteDocModal && (
        <ConfirmationModal
          showModal={!!deleteDocModal}
          onYesBtnClick={() => handleDeleteDocument()}
          onNoBtnClick={() => setDeleteDocModal(null)}
          title="Please confirm"
          message={`Are you sure you want to delete ${deleteDocModal?.fileName} file?`}
          onYesBtnText="Delete"
          onNoBtnText="Cancel"
          isProcessing={isAction === EActionItem.DELETE}
        />
      )}

      {isAction === EActionItem.CREATE ? <OverlaySpinner /> : null}
    </>
  );
};

export default AISUploadForm;
